import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../utils/api';
import { useCommunityLang } from '../context/CommunityLangContext';
import CommunityLangToggle from '../components/community/CommunityLangToggle';
import { Mail, Lock, ArrowRight, ArrowLeft, Building2, User, UserPlus, Target, Calendar, MapPin, Hash, CheckCircle2, Phone } from 'lucide-react';

const TEAL = '#4DB6AC';

const AGE_GROUPS = ['5-10', '11-15', '16-18', '18+', 'Mixed'];

export default function CommunityRegisterOrganiserPage() {
  const navigate = useNavigate();
  const { t } = useCommunityLang();
  const [form, setForm] = useState({
    organisation_name: '',
    contact_person_name: '',
    contact_number: '',
    alternative_contact_person_name: '',
    alternative_contact_number: '',
    objective: '',
    age_group: '5-10',
    location: '',
    outreach: '',
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.organisation_name.includes('@')) { setError("Organisation name can't contain '@'. Use your organisation's name, as members will see it."); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        outreach: parseInt(form.outreach, 10) || 0,
      };
      await axios.post(`${API}/community/auth/register-organiser`, payload);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('common.something_went_wrong'));
    } finally {
      setLoading(false);
    }
  };

  // ── Confirmation screen ─────────────────────────────────────
  if (submitted) {
    return (
      <div data-testid="organiser-application-confirmation"
        style={{ minHeight: '100vh', background: '#F8FAF9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <CommunityLangToggle />
        <div style={{ maxWidth: 440, width: '100%', background: '#fff', borderRadius: 16, padding: 32, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: `${TEAL}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <CheckCircle2 size={36} color={TEAL} />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1F2937', marginBottom: 12 }}>{t('org.success_title')}</h1>
          <p style={{ fontSize: 15, color: '#4B5563', lineHeight: 1.6, marginBottom: 24 }}>
            {t('org.success_body')}
          </p>
          <button data-testid="back-to-login-btn" onClick={() => navigate('/community/login')}
            style={{ width: '100%', padding: '12px', background: TEAL, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(77,182,172,0.25)' }}>
            {t('org.go_home')}
          </button>
        </div>
      </div>
    );
  }

  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none', background: '#FAFAFA' };
  const iconInputStyle = { ...inputStyle, paddingLeft: 42 };
  const selectStyle = { ...inputStyle, appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%236B7280' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center' };

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAF9', padding: '24px 16px 40px' }}>
      <CommunityLangToggle />
      <div style={{ maxWidth: 460, margin: '0 auto' }}>
        {/* Back link */}
        <button data-testid="back-link" onClick={() => navigate('/community/login')}
          style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 6, color: '#6B7280', fontSize: 13, fontWeight: 500, cursor: 'pointer', marginBottom: 16, padding: 0 }}>
          <ArrowLeft size={14} /> {t('common.back')}
        </button>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ width: 64, height: 64, borderRadius: 20, background: `linear-gradient(135deg, ${TEAL}, #2E8B7A)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <Building2 size={32} color="#fff" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1F2937', marginBottom: 4 }}>{t('org.page_title')}</h1>
          <p style={{ fontSize: 14, color: '#6B7280' }}>{t('org.page_subtitle')}</p>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          {error && (
            <div data-testid="organiser-error" style={{ padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, fontSize: 13, color: '#B91C1C', marginBottom: 16 }}>{error}</div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}><Building2 size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />{t('org.org_name_label')}</label>
              <input data-testid="input-organisation-name" required value={form.organisation_name}
                onChange={e => set('organisation_name', e.target.value)} style={inputStyle} placeholder={t('org.org_name_placeholder')} />
              <p data-testid="org-name-hint" style={{ fontSize: 11, color: '#6B7280', margin: '6px 2px 0' }}>Use your organisation's name, as members will see it.</p>
            </div>

            {/* Row 1 — primary contact: name + number (both required) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}><User size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />{t('org.contact_person_label')}</label>
                <input data-testid="input-contact-person" required value={form.contact_person_name}
                  onChange={e => set('contact_person_name', e.target.value)} style={inputStyle} placeholder={t('org.contact_person_placeholder')} />
              </div>
              <div>
                <label style={labelStyle}><Phone size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />{t('org.contact_number_label')}</label>
                <input data-testid="input-contact-number" required type="tel" value={form.contact_number}
                  onChange={e => set('contact_number', e.target.value)} style={inputStyle} placeholder={t('org.contact_number_placeholder')} />
              </div>
            </div>

            {/* Row 2 — alternative contact: name + number (both optional) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}><UserPlus size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />{t('org.alt_contact_person_label')}</label>
                <input data-testid="input-alt-contact-person" value={form.alternative_contact_person_name}
                  onChange={e => set('alternative_contact_person_name', e.target.value)} style={inputStyle} placeholder={t('org.alt_contact_person_placeholder')} />
              </div>
              <div>
                <label style={labelStyle}><Phone size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />{t('org.alt_contact_number_label')}</label>
                <input data-testid="input-alt-contact-number" type="tel" value={form.alternative_contact_number}
                  onChange={e => set('alternative_contact_number', e.target.value)} style={inputStyle} placeholder={t('org.alt_contact_number_placeholder')} />
              </div>
            </div>

            <div>
              <label style={labelStyle}><Target size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />{t('org.objective_label')}</label>
              <textarea data-testid="input-objective" required rows={4} value={form.objective}
                onChange={e => set('objective', e.target.value)} style={{ ...inputStyle, resize: 'vertical' }}
                placeholder={t('org.objective_placeholder')} />
            </div>

            <div>
              <label style={labelStyle}><Calendar size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />{t('org.age_group_label')}</label>
              <select data-testid="input-age-group" value={form.age_group}
                onChange={e => set('age_group', e.target.value)} style={selectStyle}>
                {AGE_GROUPS.map(ag => <option key={ag} value={ag}>{ag}</option>)}
              </select>
            </div>

            <div>
              <label style={labelStyle}><MapPin size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />{t('org.location_label')}</label>
              <input data-testid="input-location" required value={form.location}
                onChange={e => set('location', e.target.value)} style={inputStyle} placeholder={t('org.location_placeholder')} />
            </div>

            <div>
              <label style={labelStyle}><Hash size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />{t('org.outreach_label')}</label>
              <input data-testid="input-outreach" required type="number" min="0" value={form.outreach}
                onChange={e => set('outreach', e.target.value)} style={inputStyle} placeholder={t('org.outreach_placeholder')} />
            </div>

            <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 16, marginTop: 4 }}>
              <p style={{ fontSize: 12, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, fontWeight: 700 }}>{t('org.step_account')}</p>
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <Mail size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                <input data-testid="input-email" type="email" required placeholder={t('org.email_placeholder')}
                  value={form.email} onChange={e => set('email', e.target.value)} style={iconInputStyle} />
              </div>
              <div style={{ position: 'relative' }}>
                <Lock size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                <input data-testid="input-password" type="password" required placeholder={t('org.password_placeholder')}
                  value={form.password} onChange={e => set('password', e.target.value)} style={iconInputStyle} />
              </div>
            </div>

            <button type="submit" data-testid="submit-organiser-btn" disabled={loading}
              style={{ width: '100%', padding: '14px', background: loading ? '#9CA3AF' : TEAL, color: '#fff',
                border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: '0 4px 12px rgba(77,182,172,0.25)', transition: 'all 0.2s', marginTop: 4 }}>
              {loading ? t('common.please_wait') : (<>{t('org.submit')} <ArrowRight size={18} /></>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
